import * as config from '../config';
import Application from '../Application';
import BaseView from './BaseView';
import MemoryCache from '../MemoryCache';
import ModelLoader from '../ModelLoader';
import logging from '../logging';
import mfac from '../MaterialFactory';
import pc from '../ParametricCache';
import { Appliance, Cabinet, Room, Wall } from '../domain';
import { BoxGeometry, Group, Mesh, MeshStandardMaterial, Object3D } from 'three';
import { setOrigin } from '../geometry';

// TODO: I'd love to have some indirection here so we don't pollute other apps
// with Jarlin-specific data. We need dependency injection from the app level
// to provide access this data instead of direct import. Maybe this is moot
// if/when we are explicitly supporting cabinets throughout all apps. But un-
// mooted if we have so many cabinet SKUs that they need to be loaded at
// runtime.
import { cabinets, cabinetWidth, cabinetHeight, cabinetDepth, lineMaterial } from '../data/JarlinCatalogData';

const log = logging('view');
const ml = ModelLoader.getInstance();

const checkYPosition = (cabinet: Cabinet) => {
    if(cabinets.boolean(cabinet.sku, 'crown')) {
        switch(cabinets.string(cabinet.sku, 'model')) {
            case 'WP':
            case 'OC':
                return cabinetHeight(cabinet.sku) - 4;
            default:
                return -4;
        }
    }

    return cabinetHeight(cabinet.sku) - 4;
}

const getApplianceFor = (cabinet: Cabinet): Appliance | undefined => {
    const id = cabinet.id;
    const wall = cabinet.parent as Wall;
    if(wall) {
        for(const appliance of wall.appliances) {
            if(appliance.cabinetId == id) {
                return appliance;
            }
        }
    }
}

export default class CabinetView extends BaseView {
    declare model: Cabinet;

    implementation: Implementation;
    check: Object3D;

    constructor(model: Cabinet, app: Application) {
        super(model, app);

        this.name = 'CabinetView';

        this.init();
    }

    make() {
        const modelName = cabinets.string(this.model.sku, 'model');

        if(modelName in implementations) {
            const ViewClass = implementations[modelName];

            this.implementation = new ViewClass(this.model, this.highlights);
            this.add(this.implementation);
        } else {
            this.implementation = new FallbackCabinet(this.model, this.highlights);
            this.add(this.implementation);
        }

    }

    bind() {
        this.model.bind('userChoice', async () => {
            if(this.model.userChoice == 'location') {
                this.check = await ml.get(`${config.models}/checkmark.glb`);
                this.check.position.x = cabinetWidth(this.model.sku) - 4;
                this.check.position.y = checkYPosition(this.model);
                this.check.position.z = cabinetDepth(this.model.sku) + 1.75;
                this.add(this.check);
            } else {
                this.check?.removeFromParent();
                delete this.check;
            }
        });
    }
}

class Implementation extends Object3D {
    model: Cabinet;
    hit: Mesh;
    // this is a reference to the parent view's highlights set
    highlights: Set<Mesh>;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super();
        this.model = model;
        this.highlights = highlights;

        this.name = this.model.sku + '-impl';

        this.hit = new Mesh(
            new BoxGeometry(1, 1, 1),
            new MeshStandardMaterial({color: 0x80FFFF})
        );
        (this.hit.material as MeshStandardMaterial).transparent = true;
        (this.hit.material as MeshStandardMaterial).opacity = 0.5;
        this.hit.geometry.translate(0.5, 0.5, 0.5);
        this.hit.userData.isSelectable = true;
        this.hit.userData.isRelocatable = true;
        this.hit.name = 'Cabinet Hit';
        this.hit.visible = false;
        this.add(this.hit);

        const
            width = cabinetWidth(this.model.sku),
            height = cabinetHeight(this.model.sku),
            depth = cabinetDepth(this.model.sku);
        this.hit.scale.set(width, height, depth);
    }

    add(child: Object3D): this {
        super.add(child);
        // this visits the child itself as well as any children, so it takes
        // care of both meshes and groups or other structures
        child.traverse(obj => {
            if(obj instanceof Mesh) {
                this.highlights.add(obj);
            }
        });
        return this;
    }
}

/**
 * Abstraction of a double door
 */
class DoubleDoor extends Object3D {

    highlights: Set<Mesh>;
    line: string;
    placement: string;
    width: number;
    height: number;

    doorLeft: Mesh;
    doorRight: Mesh | undefined;
    doorRightPivot: Group = new Group();

    constructor(highlights: Set<Mesh>, line: string,
                placement: "top" | "bottom", width: number, height: number)
    {
        super();
        this.highlights = highlights;
        this.line = line;
        this.placement = placement;
        this.width = width;
        this.height = height;

        this.make().catch(log.error);
    }

    async make() {
        const
            doorWidth = (this.width - 0.25)/2,
            geometry = await pc.make(
                'Resizable',
                `${config.models}/kitchen/${this.line}-door.glb`,
                doorWidth, this.height, -1,
                0, 0, 0);

        this.doorLeft = new Mesh(geometry, lineMaterial(this.line));
        this.doorRight = new Mesh(geometry, lineMaterial(this.line));
        this.doorLeft.castShadow = this.doorLeft.receiveShadow = true;
        this.doorRight.castShadow = this.doorRight.receiveShadow = true;

        this.doorRight.position.x = -doorWidth;
        this.doorRightPivot.position.x = this.width;
        this.doorRightPivot.add(this.doorRight);
        this.add(this.doorRightPivot);
        this.add(this.doorLeft);

        this.highlights.add(this.doorRight);
        this.highlights.add(this.doorLeft);

        const
            handleLeft = new Handle(this.highlights),
            adjustment = handleLeft.style.includes('knob') ? 3 : 5,
            height = this.placement == "top"
                ? this.height - adjustment
                : adjustment;

        handleLeft.position.set(doorWidth - 1.25, height, 0.75);
        handleLeft.rotation.z = Math.PI/2;
        this.doorLeft.add(handleLeft);

        const handleRight = new Handle(this.highlights);
        handleRight.position.set(1.25, height, 0.75);
        handleRight.rotation.z = Math.PI/2;
        this.doorRight.add(handleRight);
    }

}

class SkuB extends Implementation {

    line: string;
    width: number;
    box: Mesh;
    drawerLeft: Mesh;
    drawerRight: Mesh | undefined;
    doorLeft: Mesh;
    doorRight: Mesh | undefined;
    doorRightPivot: Group = new Group();

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        this.width = cabinetWidth(model.sku);
        this.box = new Mesh();
        this.box.castShadow = this.box.receiveShadow = true;
        this.box.material = lineMaterial(this.line);
        this.make();
    }

    async make() {
        this.box.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/B-box.glb`,
            this.width, -1, -1,
            0, 0, 0
            );
        this.add(this.box);

        // note that we don't do split drawers or handles for sink cabinets
        if(cabinets.boolean(this.model.sku, 'split_drawer')) {

            const geometry = await pc.make(
                'Resizable',
                `${config.models}/kitchen/${this.line}-drawer.glb`,
                this.width/2 - 0.25, 6.5 , -1,
                0, 0, 0
            );
            this.drawerLeft = new Mesh(geometry, lineMaterial(this.line));
            this.drawerRight = new Mesh(geometry, lineMaterial(this.line));
            this.drawerLeft.castShadow = this.drawerLeft.receiveShadow = true;
            this.drawerRight.castShadow = this.drawerRight.receiveShadow = true;
            this.add(this.drawerLeft);
            this.add(this.drawerRight);

            if(cabinets.string(this.model.sku, 'appliance') != 'sink') {
                const leftHandle = new Handle(this.highlights);
                const rightHandle = new Handle(this.highlights);
                leftHandle.position.set(this.width/4, 30.75, 24.25);
                rightHandle.position.set(3*this.width/4, 30.75, 24.25);
                this.add(leftHandle);
                this.add(rightHandle);
            }
        } else {
            const geometry = await pc.make(
                'Resizable',
                `${config.models}/kitchen/${this.line}-drawer.glb`,
                this.width - 0.25, 6.5, -1,
                0, 0, 0
            );
            this.drawerLeft = new Mesh(geometry, lineMaterial(this.line));
            this.drawerLeft.castShadow = this.drawerLeft.receiveShadow = true;
            this.drawerLeft.geometry = geometry;
            this.add(this.drawerLeft);

            if(cabinets.string(this.model.sku, 'appliance') != 'sink') {
                const handle = new Handle(this.highlights);
                handle.position.set(this.width/2, 30.75, 24.5);
                this.add(handle)
            }
        }

        const
            drawerHeight = 6.5,
            doorHeight = 29 - drawerHeight;

        this.drawerLeft.position.set(0.125, 34 - drawerHeight, 24);
        this.drawerRight?.position.set(this.width / 2 + 0.125, 34 - drawerHeight, 24);

        if(cabinets.boolean(this.model.sku, 'double_door')) {
            const
                doorWidth = this.width/2 - 0.25,
                geometry = await pc.make(
                    'Resizable',
                    `${config.models}/kitchen/${this.line}-door.glb`,
                    doorWidth, doorHeight, -1,
                    0, 0, 0);

            this.doorLeft = new Mesh(geometry, lineMaterial(this.line));
            this.doorRight = new Mesh(geometry, lineMaterial(this.line));
            this.doorLeft.castShadow = this.doorLeft.receiveShadow = true;
            this.doorRight.castShadow = this.doorRight.receiveShadow = true;

            this.doorRight.position.x = -doorWidth;
            this.doorRightPivot.position.set(this.width - 0.125, 4.75, 24);
            this.doorRightPivot.add(this.doorRight);
            this.add(this.doorRightPivot);
            this.add(this.doorLeft);
            this.doorLeft.position.set(0.125, 4.75, 24);

            const handleLeft = new Handle(this.highlights);
            handleLeft.position.set(
                doorWidth - 1.25,
                handleLeft.style.includes('knob')? 20 : 18,
                0.75);
            handleLeft.rotation.z = Math.PI/2;
            this.doorLeft.add(handleLeft);

            const handleRight = new Handle(this.highlights);
            handleRight.position.set(
                1.25,
                handleRight.style.includes('knob')? 20 : 18,
                0.75);
            handleRight.rotation.z = Math.PI/2;
            this.doorRight.add(handleRight);
        } else {
            const
                doorWidth = this.width - 0.25,
                geometry = await pc.make(
                    'Resizable',
                    `${config.models}/kitchen/${this.line}-door.glb`,
                    doorWidth, doorHeight, -1,
                    0, 0, 0);

            this.doorLeft = new Mesh(geometry, lineMaterial(this.line));
            this.doorLeft.castShadow = this.doorLeft.receiveShadow = true;
            // TODO: don't hard code the depth (for possible non-Jarlin cabinets)
            this.doorLeft.position.set(0.125, 4.75, 24);
            this.add(this.doorLeft);

            const handle = new Handle(this.highlights);
            handle.position.set(
                1.25,
                handle.style.includes('knob')? 20 : 18,
                0.75);
            handle.rotation.z = Math.PI/2;
            this.doorLeft.add(handle);
        }
    }
}

class SkuBBC extends Implementation {

    line: string;
    // this is the nominal width; we will subtract an 1/8" from each side later
    doorWidth: 15 | 18;
    box: Object3D;
    door: Mesh;
    drawer: Mesh;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        // BBC39 gets 15" door, BBC42 gets 18"
        this.doorWidth = this.model.sku.indexOf('39') == -1 ? 18 : 15;
        this.door = new Mesh();
        this.drawer = new Mesh();
        this.door.material = lineMaterial(this.line);
        this.drawer.material = lineMaterial(this.line);

        this.make().catch(log.error);
    }

    async make() {
        this.box = await ml.get(`${config.models}/kitchen/${this.model.sku}.glb`);
        this.box.traverse(obj => {
            if(obj instanceof Mesh) {
                obj.material = lineMaterial(this.line);
            }
        });
        this.drawer.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-drawer.glb`,
            this.doorWidth - 0.25, 6.5, -1,
            0, 0, 0);

        const
            drawerHeight = 6.5,
            doorHeight = 29 - drawerHeight;

        this.door.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-door.glb`,
            this.doorWidth - 0.25, doorHeight, -1,
            0, 0, 0);

        // TODO: this only works with doors on the left side. Should position
        // the doors based on our position on the wall
        this.drawer.position.set(0.125, 34 - drawerHeight, 24);
        this.door.position.set(0.124, 4.75, 24);

        this.add(this.box);
        this.add(this.door);
        this.add(this.drawer);

        const drawerHandle = new Handle(this.highlights);
        drawerHandle.position.set(this.doorWidth/2, 30.75, 24.5);
        this.add(drawerHandle)

        const doorHandle = new Handle(this.highlights);
        doorHandle.position.set(
            1.25,
            doorHandle.style.includes('knob')? 20 : 18,
            0.75);
        doorHandle.rotation.z = Math.PI/2;
        this.door.add(doorHandle);
    }
}

class SkuBLS extends Implementation {

    line: string;
    width: number;
    depth: number;
    box: Mesh;

    doorLeft: Mesh;
    doorRight: Mesh;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        this.width = cabinetWidth(model.sku);
        this.depth = cabinetDepth(model.sku);

        this.box = new Mesh();
        this.box.castShadow = this.box.receiveShadow = true;
        this.box.material = lineMaterial(this.line);

        this.doorLeft = this.box.clone(true);
        this.doorRight = this.box.clone(true);

        this.make();
    }

    async make() {
        this.box.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.model.sku}.glb`,
            this.width, -1, -1,
            0, 0, 0
            );
        this.add(this.box);

        const doorWidth = this.width - 24.75;

        this.doorLeft.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-door.glb`,
            doorWidth, 29.25 , -1,
            0, 0, 0
        );
        this.doorLeft.position.set(0.125, 4.75, 24);
        this.add(this.doorLeft);

        const handle = new Handle(this.highlights);
        handle.position.set(
            1.25,
            handle.style.includes('knob')? 26.75 : 24.75,
            0.75);
        handle.rotation.z = Math.PI/2;
        this.doorLeft.add(handle);

        this.doorRight.geometry = this.doorLeft.geometry;
        this.doorRight.position.set(this.width - 24, 4.75, 24.5);
        this.doorRight.rotation.set(0, -Math.PI/2, 0);
        this.add(this.doorRight);
    }

}

class SkuBSR extends Implementation {

    line: string;
    width: number;
    box: Mesh;
    door: Mesh;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        this.width = cabinetWidth(model.sku);

        this.box = new Mesh();
        this.box.castShadow = this.box.receiveShadow = true;
        this.box.material = lineMaterial(this.line);

        this.door = this.box.clone(true);

        this.make();
    }

    async make() {
        this.box.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/B-box.glb`,
            this.width, -1, -1,
            0, 0, 0
            );
        this.add(this.box);

        this.door.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-door.glb`,
            this.width - 0.25, 29.25 , -1,
            0, 0, 0
        );
        this.door.position.set(0.125, 4.75, 24);
        this.add(this.door);

        const handle = new Handle(this.highlights);
        handle.position.set(
            (this.width - 0.25) / 2,
            handle.style.includes('knob')? 24 : 22,
            0.5);
        handle.rotation.z = Math.PI/2;
        this.door.add(handle);
    }
}

class SkuDB2 extends Implementation {

    line: string;
    width: number;
    box: Mesh;
    topDrawer: Mesh;
    bottomDrawer: Mesh;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        this.width = cabinetWidth(model.sku);
        this.box = new Mesh();
        this.box.castShadow = this.box.receiveShadow = true;
        this.box.material = lineMaterial(this.line);
        this.make();
    }

    async make() {
        this.box.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/B-box-2.glb`,
            this.width, -1, -1,
            0, 0, 0
            );
        this.add(this.box);

        const topGeometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-drawer.glb`,
            this.width - 0.25, 14.5, -1,
            0, 0, 0
        );
        this.topDrawer = new Mesh(topGeometry, lineMaterial(this.line));
        this.topDrawer.castShadow = this.topDrawer.receiveShadow = true;
        this.topDrawer.geometry = topGeometry;
        this.add(this.topDrawer);

        // The top of the top drawer should be at 34", leaving a 0.5" gap below
        // the counter top
        this.topDrawer.position.set(0.125, 34 - 14.5, 24);

        // Deep cloning will still share geometry, which is fine here
        this.bottomDrawer = this.topDrawer.clone(true);
        this.add(this.bottomDrawer);

        // the toe kick is 4.5" tall, so 4.75" leaves a 1/4" of face showing
        // below the door
        this.bottomDrawer.position.set(0.125, 4.75, 24);

        const topHandle = new Handle(this.highlights);
        topHandle.position.set((this.width - 0.25)/2, 7.25, 0.25);
        this.topDrawer.add(topHandle);

        const bottomHandle = new Handle(this.highlights);
        bottomHandle.position.set((this.width - 0.25)/2, 7.25, 0.25);
        this.bottomDrawer.add(bottomHandle);
    }
}

class SkuDB3 extends Implementation {

    line: string;
    width: number;
    box: Mesh;
    topDrawer: Mesh;
    middleDrawer: Mesh;
    bottomDrawer: Mesh;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        this.width = cabinetWidth(model.sku);
        this.box = new Mesh();
        this.box.castShadow = this.box.receiveShadow = true;
        this.box.material = lineMaterial(this.line);
        this.make();
    }

    async make() {
        this.box.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/B-box-3.glb`,
            this.width, -1, -1,
            0, 0, 0
            );
        this.add(this.box);

        const topGeometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-drawer.glb`,
            this.width - 0.25, 6.5, -1,
            0, 0, 0
        );
        this.topDrawer = new Mesh(topGeometry, lineMaterial(this.line));
        this.topDrawer.castShadow = this.topDrawer.receiveShadow = true;
        this.topDrawer.geometry = topGeometry;
        this.add(this.topDrawer);

        // The top of the top drawer should be at 34", leaving a 0.5" gap below
        // the counter top
        this.topDrawer.position.set(0.125, 34 - 6.5, 24);

        const midpoint = (this.width - 0.25)/2;

        const middleGeometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-drawer.glb`,
            this.width - 0.25, 11.25, -1,
            0, 0, 0
        );
        this.middleDrawer = new Mesh(middleGeometry, lineMaterial(this.line));
        this.middleDrawer.castShadow = this.middleDrawer.receiveShadow = true;
        this.middleDrawer.geometry = middleGeometry;
        this.add(this.middleDrawer);

        this.middleDrawer.position.set(0.125, 16, 24);

        // Deep cloning will still share geometry, which is fine here
        this.bottomDrawer = this.middleDrawer.clone(true);
        this.add(this.bottomDrawer);

        // the toe kick is 4.5" tall, so 4.75" leaves a 1/4" of face showing
        // below the door
        this.bottomDrawer.position.set(0.125, 4.75, 24);

        const topHandle = new Handle(this.highlights);
        topHandle.position.set(midpoint, 3.25, 0.25);
        this.topDrawer.add(topHandle);

        const middleHandle = new Handle(this.highlights);
        middleHandle.position.set(midpoint, 5.625, 0.25);
        this.middleDrawer.add(middleHandle);

        const bottomHandle = new Handle(this.highlights);
        bottomHandle.position.set(midpoint, 5.625, 0.25);
        this.bottomDrawer.add(bottomHandle);
    }
}

class SkuSBS extends Implementation {

    line: string;
    box: Mesh;
    doorLeft: Mesh;
    doorRight: Mesh;
    doorRightPivot: Group = new Group();
    sink: Object3D;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        this.box = new Mesh();
        this.box.castShadow = this.box.receiveShadow = true;
        this.box.material = lineMaterial(this.line);

        this.doorLeft = this.box.clone(true);
        this.doorRight = this.box.clone(true);

        this.make();
    }

    async make() {
        // we're not actually resizing this, but I want to cache the geometry
        // and I don't care about the scene.
        this.box.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/SB36-S.glb`,
            -1, -1, -1,
            0, 0, 0
            );
        this.add(this.box);

        this.doorLeft.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-door.glb`,
            17.75, 20.25, -1,
            0, 0, 0
        );
        this.doorLeft.position.set(0.125, 4.75, 24);
        this.add(this.doorLeft);

        const handleLeft = new Handle(this.highlights);
        handleLeft.position.set(
            16.5,
            handleLeft.style.includes('knob')? 17.25 : 15.25,
            0.75);
        handleLeft.rotation.z = Math.PI/2;
        this.doorLeft.add(handleLeft);

        this.doorRight.geometry = this.doorLeft.geometry;
        this.doorRight.position.set(-17.75, 0, 0);
        this.doorRightPivot.add(this.doorRight);
        this.doorRightPivot.position.set(35.875, 4.75, 24);
        this.add(this.doorRightPivot);

        const handleRight = new Handle(this.highlights);
        handleRight.position.set(
            1.25,
            handleRight.style.includes('knob')? 17.25 : 15.25,
            0.75);
        handleRight.rotation.z = Math.PI/2;
        this.doorRight.add(handleRight);
    }
}

class SkuW extends Implementation {
    line: string;
    width: number;
    height: number;
    depth: number;
    box: Mesh;
    doorLeft: Mesh;
    doorRight: Mesh | undefined;
    doorRightPivot: Group = new Group();

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        this.width = cabinetWidth(model.sku);
        this.height = cabinetHeight(model.sku);
        this.depth = cabinetDepth(model.sku);
        this.box = new Mesh();
        this.box.castShadow = this.box.receiveShadow = true;
        this.box.material = lineMaterial(this.line);

        this.make();
    }

    async make() {
        this.box.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/W-box.glb`,
            this.width, this.height, this.depth,
            0, 1, 0
            );
        this.add(this.box);

        this.hit.position.y = -this.height;

        const doorHeight = this.height - 0.25;

        if(cabinets.boolean(this.model.sku, 'double_door')) {
            const
                doorWidth = this.width/2 - 0.25,
                geometry = await pc.make(
                    'Resizable',
                    `${config.models}/kitchen/${this.line}-door.glb`,
                    doorWidth, doorHeight, -1,
                    0, 1, 0);
            this.doorLeft = new Mesh(geometry, lineMaterial(this.line));
            this.doorRight = new Mesh(geometry, lineMaterial(this.line));
            this.doorLeft.castShadow = this.doorLeft.receiveShadow = true;
            this.doorRight.castShadow = this.doorRight.receiveShadow = true;

            this.doorRight.position.x = -doorWidth;
            this.doorRightPivot.position.set(this.width - 0.125, -0.125, this.depth);
            this.doorLeft.position.set(0.125, -0.125, this.depth);

            this.doorRightPivot.add(this.doorRight);
            this.add(this.doorRightPivot);
            this.add(this.doorLeft);

            const handleLeft = new Handle(this.highlights);
            handleLeft.position.set(
                doorWidth - 1.25,
                -doorHeight + (handleLeft.style.includes('knob') ? 3 : 5),
                0.75
                );
            handleLeft.rotation.z = Math.PI/2;
            this.doorLeft.add(handleLeft);

            const handleRight = new Handle(this.highlights);
            handleRight.position.set(
                1.25,
                -doorHeight + (handleLeft.style.includes('knob') ? 3 : 5),
                0.75);
            handleRight.rotation.z = Math.PI/2;
            this.doorRight.add(handleRight);
        } else {

            const
                doorWidth = this.width - 0.25,
                geometry = await pc.make(
                    'Resizable',
                    `${config.models}/kitchen/${this.line}-door.glb`,
                    doorWidth, doorHeight, -1,
                    0, 1, 0);
            this.doorLeft = new Mesh(geometry, lineMaterial(this.line));
            this.doorLeft.castShadow = this.doorLeft.receiveShadow = true;
            this.doorLeft.position.set(0.125, -0.125, this.depth);
            this.add(this.doorLeft);

            const handle = new Handle(this.highlights);
            handle.position.set(
                1.25,
                -doorHeight + (handle.style.includes('knob') ? 3 : 5),
                0.75);
            handle.rotation.z = Math.PI/2;
            this.doorLeft.add(handle);
        }
    }
}

class SkuWDC extends Implementation {

    line: string;
    height: number;
    box: Mesh;
    door: Mesh;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        this.height = cabinetHeight(model.sku);

        this.box = new Mesh();
        this.box.castShadow = this.box.receiveShadow = true;
        this.box.material = lineMaterial(this.line);

        this.hit.position.y = -this.height;

        this.door = this.box.clone(true);

        this.make();
    }

    async make() {
        const model = cabinets.string(this.model.sku, 'model');
        this.box.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${model}-box.glb`,
            -1, this.height, -1,
            0, 1, 0
            );
        this.add(this.box);

        // If we rotate 1/8" by 45 degrees, its x and y components are...
        const eighthInchBy45 = Math.cos(Math.PI/4) * 0.125;

        // Math.sqrt(12**2 + 12**2) - 0.25
        const doorWidth: number = 16.72056275;
        switch(model) {
            case 'WDC12':
                this.door.position.set(
                    eighthInchBy45,
                    -0.25,
                    12 + eighthInchBy45);
                break;
            case 'WDC15':
                this.door.position.set(
                    eighthInchBy45,
                    -0.25,
                    15 + eighthInchBy45);
                break;

            default:
                log.error(`SkuWDC.make unknown model ${model}`);;
        }

        this.door.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-door.glb`,
            doorWidth, this.height - 0.5, -1,
            0, 1, 0
        );
        this.door.rotation.set(0, -Math.PI/4, 0);
        this.add(this.door);

        const
            handle = new Handle(this.highlights),
            adjustment = handle.style.includes('knob')? 3 : 5,
            handleHeight = -this.height + 0.5 + adjustment;
        handle.position.set(
            1.25,
            handleHeight,
            0.75);
        handle.rotation.z = Math.PI/2;
        this.door.add(handle);
    }

}

class SkuWP extends Implementation {

    line: string;
    width: number;
    height: number;
    box: Mesh;
    doorTop: Mesh | DoubleDoor;
    doorBottom: Mesh | DoubleDoor;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        this.width = cabinetWidth(model.sku);
        this.height = cabinetHeight(model.sku);
        this.box = new Mesh();
        this.box.castShadow = this.box.receiveShadow = true;
        this.box.material = lineMaterial(this.line);
        this.make();
    }

    async make() {
        this.box.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/WP-box.glb`,
            this.width, this.height, -1,
            0, 0, 0
            );
        this.add(this.box);


        if(cabinets.boolean(this.model.sku, 'double_door')) {
            this.doorTop = new DoubleDoor(
                this.highlights,
                this.line,
                "bottom", // handle placement
                this.width - 0.25,
                this.height - 54.5
            );
            this.add(this.doorTop);
            this.doorTop.position.set(0.125, 54.25, 24);

            this.doorBottom = new DoubleDoor(
                this.highlights,
                this.line,
                "top", // handle placement
                this.width - 0.25,
                49.25
            );
            this.add(this.doorBottom);
            this.doorBottom.position.set(0.125, 4.75, 24);
        } else {
            const
                doorWidth = this.width - 0.25,
                geometryTop = await pc.make(
                    'Resizable',
                    `${config.models}/kitchen/${this.line}-door.glb`,
                    doorWidth, this.height - 54.5, -1,
                    0, 0, 0),
                geometryBottom = await pc.make(
                    'Resizable',
                    `${config.models}/kitchen/${this.line}-door.glb`,
                    doorWidth, 49.25, -1,
                    0, 0, 0);

            this.doorTop = new Mesh(geometryTop, lineMaterial(this.line));
            this.doorTop.castShadow = this.doorTop.receiveShadow = true;
            this.doorTop.position.set(0.125, 54.25, 24);
            this.add(this.doorTop);

            this.doorBottom = new Mesh(geometryBottom, lineMaterial(this.line));
            this.doorBottom.castShadow = this.doorBottom.receiveShadow = true;
            this.doorBottom.position.set(0.125, 4.75, 24);
            this.add(this.doorBottom);

            const
                topHandle = new Handle(this.highlights),
                adjustment = topHandle.style.includes('knob')? 3 : 5;
            topHandle.position.set(1.25, adjustment, 0.25);
            topHandle.rotation.z = Math.PI/2;
            this.doorTop.add(topHandle);

            const bottomHandle = new Handle(this.highlights);
            bottomHandle.position.set(1.25, 49.25 - adjustment, 0.25);
            bottomHandle.rotation.z = Math.PI/2;
            this.doorBottom.add(bottomHandle);
        }
    }
}

class SkuOC extends Implementation {
    line: string;
    width: number;
    height: number;

    box: Mesh;
    door: DoubleDoor;
    topDrawer: Mesh;
    middleDrawer: Mesh;
    bottomDrawer: Mesh;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);

        this.line = (model.parent.parent as Room).line;
        this.width = cabinetWidth(model.sku);
        this.height = cabinetHeight(model.sku);
        this.box = new Mesh();
        this.box.castShadow = this.box.receiveShadow = true;
        this.box.material = lineMaterial(this.line);
        this.make();
    }

    async make() {
        this.box.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/OC-box.glb`,
            this.width, this.height, -1,
            0, 0, 0
            );
        this.add(this.box);

        this.door = new DoubleDoor(
            this.highlights,
            this.line,
            "bottom", // handle placement
            this.width - 0.25,
            this.height - 72.5
        );
        this.door.position.set(0.125, 72, 24);
        this.add(this.door);

        const topGeometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-drawer.glb`,
            this.width - 0.25, 6.5, -1,
            0, 0, 0
        );
        this.topDrawer = new Mesh(topGeometry, lineMaterial(this.line));
        this.topDrawer.castShadow = this.topDrawer.receiveShadow = true;
        this.topDrawer.geometry = topGeometry;
        this.add(this.topDrawer);

        // The top of the top drawer should be at 34", leaving a 0.5" gap below
        // the counter top
        this.topDrawer.position.set(0.125, 34 - 6.5, 24);

        const midpoint = (this.width - 0.25)/2;

        const middleGeometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/${this.line}-drawer.glb`,
            this.width - 0.25, 11.25, -1,
            0, 0, 0
        );
        this.middleDrawer = new Mesh(middleGeometry, lineMaterial(this.line));
        this.middleDrawer.castShadow = this.middleDrawer.receiveShadow = true;
        this.middleDrawer.geometry = middleGeometry;
        this.add(this.middleDrawer);

        this.middleDrawer.position.set(0.125, 16, 24);

        // Deep cloning will still share geometry, which is fine here
        this.bottomDrawer = this.middleDrawer.clone(true);
        this.add(this.bottomDrawer);

        // the toe kick is 4.5" tall, so 4.75" leaves a 1/4" of face showing
        // below the door
        this.bottomDrawer.position.set(0.125, 4.75, 24);

        const topHandle = new Handle(this.highlights);
        topHandle.position.set(midpoint, 3.25, 0.25);
        this.topDrawer.add(topHandle);

        const middleHandle = new Handle(this.highlights);
        middleHandle.position.set(midpoint, 5.625, 0.25);
        this.middleDrawer.add(middleHandle);

        const bottomHandle = new Handle(this.highlights);
        bottomHandle.position.set(midpoint, 5.625, 0.25);
        this.bottomDrawer.add(bottomHandle);

    }
}

class SkuBF extends Implementation {
    declare model: Cabinet;
    mesh: Mesh;

    line: string;
    width: number;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.line = (model.parent.parent as Room).line;
        this.width = cabinetWidth(this.model.sku);
        this.mesh = new Mesh();
        this.mesh.castShadow = this.mesh.receiveShadow = true;
        this.mesh.material = lineMaterial(this.line);
        this.make().catch(log.error);
    }

    async make() {
        this.mesh.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/BF.glb`,
            this.width, -1, -1,
            0, 0, 1);

        this.mesh.position.set(0, 0, 24);
        this.add(this.mesh);
    }
}

export class SkuWF extends Implementation {
    declare model: Cabinet;
    mesh: Mesh;

    line: string;
    width: number;
    height: number;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);

        this.line = (model.parent.parent as Room).line;
        this.width = cabinetWidth(this.model.sku);
        this.height = cabinetHeight(this.model.sku);
        this.mesh = new Mesh();
        this.mesh.castShadow = this.mesh.receiveShadow = true;
        this.mesh.material = lineMaterial(this.line);

        this.hit.scale.y = this.model.position.z;

        this.make().catch(log.error);
    }

    async make() {
        this.mesh.geometry = await pc.make(
            'Resizable',
            `${config.models}/kitchen/WF.glb`,
            this.width, this.height, -1,
            0, 1, 1);

        this.add(this.mesh);
    }
}

const implementations: Record<string, typeof Implementation> = {
    "B": SkuB,
    "BBC": SkuBBC,
    "BF": SkuBF,
    "BLS": SkuBLS,
    "BSR": SkuBSR,
    "DB2": SkuDB2,
    "DB3": SkuDB3,
    "OC": SkuOC,
    "SBS": SkuSBS,
    "W": SkuW,
    "WDC12": SkuWDC,
    "WDC15": SkuWDC,
    "WF": SkuWF,
    "WP": SkuWP,
};

export class FallbackCabinet extends Implementation {

    declare model: Cabinet;
    mesh: Mesh;

    constructor(model: Cabinet, highlights: Set<Mesh>) {
        super(model, highlights);
        this.make();
        this.bind();
    }

    make(): void {
        this.mesh = new Mesh(
            new BoxGeometry(1, 1, 1),
            new MeshStandardMaterial({color: 0x808080})
        );
        this.mesh.castShadow = this.mesh.receiveShadow = true;

        if(cabinets.boolean(this.model.sku, 'crown')) {
            this.mesh.geometry.translate(0.5, -0.5, 0.5);
        }
        else {
            this.mesh.geometry.translate(0.5, 0.5, 0.5);
        }

        this.add(this.mesh);
    }

    bind(): void {
        const update = this.updateAppearance.bind(this);
        this.model.bind('sku', update);
    }

    updateAppearance() {
        const
            width = cabinetWidth(this.model.sku),
            height = cabinetHeight(this.model.sku),
            depth = cabinetDepth(this.model.sku);
        this.mesh.scale.set(width, height, depth);
    }

}

class Handle extends Object3D {

    highlights: Set<Mesh>;
    style: string;

    constructor(highlights: Set<Mesh>) {
        super();
        this.name = 'CabinetHandle';
        this.highlights = highlights;

        let data: Record<string, unknown>
        const selectionsStr = localStorage.getItem('kitchen-tool-selections');
        if(selectionsStr) {
            data = JSON.parse(selectionsStr);
        }
        this.style = String(data?.pull ?? 'pull1');

        this.init();
    }

    async init() {
        const object = (await Handle.load(this.style)).clone();

        object.traverse(child => {
            if(child instanceof Mesh) {
                this.highlights.add(child);
            }
        });

        this.add(object);
    }

    static async load(style: string) {
        const filename = {
            "pull1": "d-hpull7.obj",
            "pull2": "d-hpull7.obj",
            "knob1": "d-hpull11.obj",
            "knob2": "d-hpull11.obj",
        }[style];

        const material = {
            "pull1": "Chrome",
            "pull2": "Brass",
            "knob1": "Chrome",
            "knob2": "Brass",
        }[style];

        const object = (await ml.get(`${config.models}/${filename}`)).clone(true);

        object.traverse(child => {
            if(child instanceof Mesh) {
                setOrigin(child.geometry, 0.5, 0.5, 0);
                child.castShadow = child.receiveShadow = true;
                child.material = mfac.make(material);
            }
        });

        return object;
    }

}
Handle.load = MemoryCache.memoize(Handle.load);
